import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useAuth } from '../AuthContext';
import { listBuddies } from '../api';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

// Simple default Leaflet icon fix for bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export default function CatchMapPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [catches, setCatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [center, setCenter] = useState(null); // { lat, lon }
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [buddies, setBuddies] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]); // ids of me/buddies to show; empty = all

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/catches`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json().catch(() => []);
        if (!cancelled) {
          setCatches(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) setCatches([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    // Try to center on current user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!cancelled) {
            setCenter({ lat: pos.coords.latitude, lon: pos.coords.longitude });
          }
        },
        () => {
          // ignore errors; we'll fall back below
        },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }

    return () => {
      cancelled = true;
    };
  }, [token]);

  // Disable page scrolling while the Catch Map page is active
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // Load buddies for "my buddies" filter option
  useEffect(() => {
    if (!token) {
      setBuddies([]);
      return;
    }
    let cancelled = false;
    const loadBuddies = async () => {
      try {
        const data = await listBuddies(token);
        if (!cancelled) {
          setBuddies(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) setBuddies([]);
      }
    };
    loadBuddies();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Fallback center if no geolocation
  const effectiveCenter = useMemo(() => {
    if (center) return center;
    const withCoords = catches.filter((c) =>
      typeof c.latitude === 'number' && typeof c.longitude === 'number'
    );
    if (withCoords.length > 0) {
      const avgLat =
        withCoords.reduce((sum, c) => sum + c.latitude, 0) / withCoords.length;
      const avgLon =
        withCoords.reduce((sum, c) => sum + c.longitude, 0) / withCoords.length;
      return { lat: avgLat, lon: avgLon };
    }
    // Default to some neutral center (central Europe)
    return { lat: 50.0, lon: 10.0 };
  }, [center, catches]);

  const users = useMemo(() => {
    return buddies;
  }, [buddies]);

  const buddyIdSet = useMemo(() => new Set(users.map((u) => u.id)), [users]);
  const selectedIdSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);

  const filteredCatches = useMemo(() => {
    let list = catches;
    if (selectedIdSet.size > 0) {
      list = list.filter((c) => selectedIdSet.has(c.user_id));
    }
    return list;
  }, [catches, selectedIdSet]);

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        pt: 2,
        pb: 0,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, mb: 2, flexShrink: 0 }}>
        <Typography variant="h5" gutterBottom>
          Catch Map
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setFilterDialogOpen(true)}
          >
            {(() => {
              if (selectedIdSet.size === 0) return 'All users';
              if (user?.id && selectedIdSet.size === 1 && selectedIdSet.has(user.id)) {
                return 'Me';
              }
              if (
                buddyIdSet.size > 0 &&
                selectedIdSet.size === buddyIdSet.size &&
                [...buddyIdSet].every((id) => selectedIdSet.has(id))
              ) {
                return `My buddies (${buddyIdSet.size})`;
              }
              return `Selected (${selectedIdSet.size})`;
            })()}
          </Button>
        </Box>
      </Box>
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)}>
        <DialogTitle>Filter catches</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSelectedUserIds([])}
            >
              All users
            </Button>
            {user && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedUserIds([user.id])}
              >
                Me only
              </Button>
            )}
            {users.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedUserIds(users.map((u) => u.id))}
              >
                All buddies
              </Button>
            )}
          </Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Select people
          </Typography>
          <List dense>
            {user && (
              <ListItem
                button
                selected={selectedIdSet.has(user.id)}
                onClick={() => {
                  setSelectedUserIds((prev) =>
                    prev.includes(user.id) ? prev.filter((id) => id !== user.id) : [...prev, user.id]
                  );
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={(() => {
                      const pic = user.profile_picture;
                      if (!pic) return undefined;
                      if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
                      const normalized = pic.startsWith('/') ? pic.slice(1) : pic;
                      return `${API_URL}/${normalized}`;
                    })()}
                  />
                </ListItemAvatar>
                <ListItemText primary={user.nickname || 'Me'} secondary="Me" />
              </ListItem>
            )}
            {users.map((u) => (
              <ListItem
                key={u.id}
                button
                selected={selectedIdSet.has(u.id)}
                onClick={() => {
                  setSelectedUserIds((prev) =>
                    prev.includes(u.id) ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                  );
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={(() => {
                      const pic = u.profile_picture;
                      if (!pic) return undefined;
                      if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
                      const normalized = pic.startsWith('/') ? pic.slice(1) : pic;
                      return `${API_URL}/${normalized}`;
                    })()}
                  />
                </ListItemAvatar>
                <ListItemText primary={u.nickname} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <MapContainer
            center={[effectiveCenter.lat, effectiveCenter.lon]}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            />
            {filteredCatches.map((c) => (
              typeof c.latitude === 'number' && typeof c.longitude === 'number' && (
                <Marker key={c.id} position={[c.latitude, c.longitude]}>
                  <Popup>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                        onClick={() => {
                          if (c.user_id) navigate(`/profile/${c.user_id}`);
                        }}
                      >
                        {c.user_profile_picture && (
                          <img
                            src={(() => {
                              const pic = c.user_profile_picture;
                              if (!pic) return undefined;
                              if (pic.startsWith('http://') || pic.startsWith('https://')) return pic;
                              const normalized = pic.startsWith('/') ? pic.slice(1) : pic;
                              return `${API_URL}/${normalized}`;
                            })()}
                            alt={c.user_nickname}
                            style={{ width: 32, height: 32, borderRadius: '50%' }}
                          />
                        )}
                        <Typography variant="subtitle2">{c.user_nickname}</Typography>
                      </Box>
                      <Typography variant="body2">
                        {c.species} {c.size ? `- ${c.size} cm` : ''}
                      </Typography>
                      {c.location && (
                        <Typography variant="caption" color="text.secondary">
                          {c.location}
                        </Typography>
                      )}
                    </Box>
                  </Popup>
                </Marker>
              )
            ))}
          </MapContainer>
        )}
      </Box>
    </Container>
  );
}
